import { HttpClient } from "@angular/common/http";
import {Injectable} from "@angular/core";
import {
  ActionResult, Competitor, ControlPointTime, RaceStartItem,
  StartRaces, CompetitorSaveControlPointResult, AddChipTime, SaveOrUpdateRaceStartResponse, RaceSettings
} from "@model";
import {EnvironmentService} from "./environment.service";
import {map, Observable, timeout, catchError, of} from "rxjs";

@Injectable({providedIn: 'root'})
export class MeasurementsService {

  constructor(private http: HttpClient, private environmentService: EnvironmentService) {
  }

  saveOrUpdateRaceStart(eventId: number, startRaces: StartRaces, onSuccess: (response: SaveOrUpdateRaceStartResponse[]) => void, onError: () => void): void {
    this.http.post<ActionResult<SaveOrUpdateRaceStartResponse[]>>(this.environmentService.getApiUrl() + `/api/measurements/race/start?eventId=${eventId}`, startRaces)
      .subscribe(actionResult => actionResult.resultCode === 0 && actionResult.result ? onSuccess(actionResult.result) : onError());
  }

  findRaceStartItems(raceId: number): Observable<RaceStartItem[]> {
    return this.http.get<ActionResult<RaceStartItem[]>>(`${this.environmentService.getApiUrl()}/api/measurements/race/find?raceId=${raceId}`).pipe(
      map(actionResult => actionResult.resultCode !== -1 ? (actionResult.result ?? []) : [])
    );
  }

  findControlPointTimes(competitorId: number, raceId: number): Observable<ControlPointTime[]> {
    return this.http.get<ActionResult<ControlPointTime[]>>(`${this.environmentService.getApiUrl()}/api/measurements/findcontrolpointtimes?competitorId=${competitorId}&raceId=${raceId}`).pipe(
      map(actionResult => actionResult.resultCode !== -1 ? (actionResult.result ?? []) : [])
    );
  }

  findControlPointTimesForTeamMembers(competitorId: number, raceId: number): Observable<Competitor[]> {
    return this.http.get<ActionResult<ControlPointTime[]>>(`${this.environmentService.getApiUrl()}/api/measurements/findteammembertimes?competitorId=${competitorId}&raceId=${raceId}`).pipe(
      map(actionResult => actionResult.resultCode !== -1 ? (actionResult.result ?? []) : [])
    );
  }

  saveOrUpdateCompetitorControlPoints(competitor: Competitor, eventId: number, onSuccess: (result: CompetitorSaveControlPointResult[]) => void, onError: () => void): void {
    this.http.post<ActionResult<CompetitorSaveControlPointResult[]>>(this.environmentService.getApiUrl() + `/api/measurements/competitor/controlpoints/start?eventId=${eventId}`, competitor)
      .subscribe(actionResult => {
        actionResult.resultCode === 0 && actionResult.result ? onSuccess(actionResult.result) : onError()
      });
  }

  saveCompetitorsControlPoints(competitors: Competitor[], eventId: number, onSuccess: (competitors: CompetitorSaveControlPointResult[]) => void, onError: () => void): void {
    this.http.post<ActionResult<CompetitorSaveControlPointResult[]>>(this.environmentService.getApiUrl() + `/api/measurements/competitors/controlpoints/start?eventId=${eventId}`, competitors)
      .subscribe(actionResult => actionResult.resultCode === 0 ? onSuccess(actionResult.result ?? []) : onError());
  }

  deleteCompetitorCompetitorControlPoints(competitor: Competitor, onSuccess: () => void, onError: () => void): void {
    this.http.post<ActionResult<Boolean>>(this.environmentService.getApiUrl() + `/api/measurements/competitor/controlpoints/cancel`, competitor)
        .subscribe(actionResult => actionResult.resultCode === 0 && actionResult.result ? onSuccess() : onError());
  }


  saveStartNumberControlPoints(competitors: Competitor[], eventId: number, onSuccess: (competitors: CompetitorSaveControlPointResult[]) => void, onError: () => void): void {
    this.http.post<ActionResult<CompetitorSaveControlPointResult[]>>(this.environmentService.getApiUrl() + `/api/measurements/competitors/controlpoints/startNumber?eventId=${eventId}`, competitors)
      .subscribe(actionResult => actionResult.resultCode === 0 ? onSuccess(actionResult.result ?? []) : onError());
  }

  stopCompetitor(competitorId: number, raceId: number, chronometarRace:boolean, onSuccess: () => void, onError: () => void): void {
    this.http.delete<ActionResult<boolean>>(`${this.environmentService.getApiUrl()}/api/measurements/competitor/stop?competitorId=${competitorId}&raceId=${raceId}&chronometarRace=${chronometarRace}`)
      .subscribe(actionResult => actionResult.resultCode === 0 ? onSuccess() : onError());
  }

  startCompetitor(competitor: Competitor, chronometarRace:boolean, onSuccess: () => void, onError: () => void): void {
    this.http.post<ActionResult<boolean>>(`${this.environmentService.getApiUrl()}/api/measurements/competitor/start?chronometarRace=${chronometarRace}`, competitor)
      .subscribe(actionResult => actionResult.resultCode === 0 ? onSuccess() : onError());
  }

  refreshResults(eventId: number, onSuccess: () => void, onError: () => void):void {
    this.http.post<ActionResult<boolean>>(`${this.environmentService.getApiUrl()}/api/measurements/results/refresh?eventId=${eventId}`, null)
        .subscribe(actionResult => actionResult.resultCode === 0 ? onSuccess() : onError());
  }


  saveOrUpdateCompetitorLaps(competitor: Competitor, eventId: number, onSuccess: (result: CompetitorSaveControlPointResult[]) => void, onError: () => void): void {
    this.http.post<ActionResult<CompetitorSaveControlPointResult[]>>(this.environmentService.getApiUrl() + `/api/measurements/competitor/laps/edit?eventId=${eventId}`, competitor)
      .subscribe(actionResult => actionResult.resultCode === 0 && actionResult.result ? onSuccess(actionResult.result) : onError());
  }

  saveOrUpdateCompetitorsLaps(competitors: Competitor[],  eventId: number, onSuccess: (competitors: CompetitorSaveControlPointResult[]) => void, onError: () => void): void {
    this.http.post<ActionResult<CompetitorSaveControlPointResult[]>>(this.environmentService.getApiUrl() + `/api/measurements/competitors/laps/edit?eventId=${eventId}`, competitors)
      .subscribe(actionResult => actionResult.resultCode === 0 ? onSuccess(actionResult.result ?? []) : onError());
  }

  findLapsTimes(competitorId: number, raceId: number): Observable<ControlPointTime[]> {
    return this.http.get<ActionResult<ControlPointTime[]>>(`${this.environmentService.getApiUrl()}/api/measurements/competitors/laps?competitorId=${competitorId}&raceId=${raceId}`).pipe(
      map(actionResult => actionResult.resultCode !== -1 ? (actionResult.result ?? []) : [])
    );
  }

  addChipTime(addChipTime:AddChipTime, roundRace: boolean, onSuccess: (competitors: CompetitorSaveControlPointResult[]) => void, onError: () => void): void {
    this.http.post<ActionResult<CompetitorSaveControlPointResult[]>>(`${this.environmentService.getApiUrl()}/api/measurements/addChipTime?roundRace=${roundRace}`, addChipTime)
      .subscribe(actionResult => actionResult.resultCode === 0 ? onSuccess(actionResult.result ?? []) : onError());
  }


  ping(): Observable<boolean | undefined> {
    const PING_TIMEOUT = 4000;

    return this.http.get<ActionResult<boolean>>(`${this.environmentService.getApiUrl()}/api/public/ping`)
        .pipe(
            timeout(PING_TIMEOUT),
            map(value => {
              return value?.result;
            }),
            catchError(error => {
              return of(false);
            })
        );
  }}
